'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { Button } from '@/components/primitives/Button';
import { CloseIcon } from '@/components/primitives/icons/CloseIcon';
import { HamburgerIcon } from '@/components/primitives/icons/HamburgerIcon';
import {
  GA_CTA_ID,
  GA_CTA_PLACEMENT,
  GA_EVENTS,
  GA_LINK_PLACEMENT,
  GA_NAV_METHOD,
  GA_PARAMS,
} from '@/constants/analytics';
import { MOBILE_NAV_CHAPTERS } from '@/constants/mobileNav';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { useScrollLock } from '@/hooks/useScrollLock';
import { trackEvent, trackOutboundClick } from '@/lib/analytics/trackEvent';

import styles from './MobileMenu.module.css';
import { NavbarLanguageMenu } from './NavbarLanguageMenu';
import { useMobileMenuChapterNavigation } from './useMobileMenuChapterNavigation';

import type { MobileMenuMessages } from './mobileMenuMessages';
import type { Chapter } from '@/components/layout/Section';
import type { Locale } from '@/lib/i18n';

type MobileMenuProps = {
  locale: Locale;
  messages: MobileMenuMessages;
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function MobileMenu({ locale, messages }: MobileMenuProps) {
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  const closeMenu = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useScrollLock(open);
  const navigateToChapter = useMobileMenuChapterNavigation({
    isOpen: open,
    onClose: closeMenu,
    reducedMotion: prefersReducedMotion,
  });

  useEffect(() => {
    const header = triggerRef.current?.closest('header');
    if (!header) return;

    if (open) {
      header.setAttribute('data-menu-open', '');
    } else {
      header.removeAttribute('data-menu-open');
    }

    return () => {
      header.removeAttribute('data-menu-open');
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const panel = panelRef.current;
    const trigger = triggerRef.current;
    if (!panel) return;

    const panelFocusable = [
      ...panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ];
    // Include header close control so Tab can reach the X (Figma chrome).
    const focusable = trigger
      ? [trigger, ...panelFocusable]
      : panelFocusable;
    panelFocusable[0]?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        return;
      }

      if (event.key !== 'Tab' || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeMenu, open]);

  const handleChapterClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    id: Chapter,
  ) => {
    event.preventDefault();
    trackEvent(GA_EVENTS.navClick, {
      [GA_PARAMS.chapterId]: id,
      [GA_PARAMS.method]: GA_NAV_METHOD.mobileMenu,
    });
    navigateToChapter(id);
  };

  return (
    <div className={styles.root}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={open ? messages.navbar.closeMenu : messages.navbar.openMenu}
        onClick={() => setOpen((current) => !current)}
      >
        {open ? <CloseIcon size={24} /> : <HamburgerIcon size={24} />}
      </button>

      <div
        ref={panelRef}
        id={panelId}
        className={[styles.panel, open ? styles.panelOpen : '']
          .filter(Boolean)
          .join(' ')}
        hidden={!open}
        role="dialog"
        aria-modal="true"
        aria-label={messages.navbar.mobileNavLabel}
        data-lenis-prevent=""
      >
        <nav className={styles.chapters} aria-label={messages.nav.stickyLabel}>
          <ul className={styles.chapterList}>
            {MOBILE_NAV_CHAPTERS.map((id) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  className={styles.chapterLink}
                  onClick={(event) => handleChapterClick(event, id)}
                >
                  {messages.nav[id]}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.actions}>
          <ul className={styles.linkList}>
            <li>
              <Button
                href={messages.navbar.datazoneUrl}
                variant="secondary"
                size="label"
                colour="white"
                className={styles.actionLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackOutboundClick({
                    href: messages.navbar.datazoneUrl,
                    linkPlacement: GA_LINK_PLACEMENT.datazoneNav,
                  });
                }}
              >
                {messages.navbar.datazone}
              </Button>
            </li>
            <li>
              <Button
                href={messages.navbar.downloadPdfUrl}
                variant="secondary"
                size="label"
                colour="white"
                className={styles.actionLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent(GA_EVENTS.ctaClick, {
                    [GA_PARAMS.ctaId]: GA_CTA_ID.downloadReport,
                    [GA_PARAMS.placement]: GA_CTA_PLACEMENT.mobileMenu,
                  });
                }}
              >
                {messages.navbar.downloadPdf}
              </Button>
            </li>
          </ul>

          <Button
            href={messages.navbar.supportUrl}
            variant="primary"
            size="large"
            colour="blue"
            className={styles.cta}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent(GA_EVENTS.ctaClick, {
                [GA_PARAMS.ctaId]: GA_CTA_ID.support,
                [GA_PARAMS.placement]: GA_CTA_PLACEMENT.mobileMenu,
              });
            }}
          >
            {messages.navbar.supportCta}
          </Button>

          <div className={styles.language}>
            <NavbarLanguageMenu
              locale={locale}
              languageSwitcherLabel={messages.nav.languageSwitcherLabel}
              tone="onBlue"
              compact
            />
          </div>
        </div>
      </div>
    </div>
  );
}
